<template>
  <v-btn
    :ripple="false"
    :block="true"
    :color="
      outOfOffice
        ? 'orange-lighten-5'
        : project?.projectId === 'SYSTEM_LUNCH-BREAK'
        ? 'warning'
        : color
    "
    :variant="variant"
    height="auto"
    size="x-large"
    width="90%"
    :style="[customStyles, pointerEventsStyle]"
    @click="handleClick"
    class="custom-button-width"
  >
    <span
      style="
        display: flex;
        flex-direction: column;
        align-items: start;
        width: 100%;
      "
    >
      <!-- Main Display Content -->
      <span
        style="
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          gap: 15px;
          width: 100%;
          align-items: center;
        "
      >
        <span v-if="userName" width="100%">{{ fullName }}</span>
        <span
          v-if="userDescriptionDisplay && hasDescription"
          style="font-size: 12px !important"
        >
          <v-icon style="margin-top: -10px" v-if="!showUserDescription"
            >mdi-eye-outline</v-icon
          >
          <v-icon style="margin-top: -10px" v-else>mdi-eye-off-outline</v-icon>
        </span>
        <span style="padding: 15px 0" v-if="outOfOffice">Out Of Office</span>
      </span>
      <span
        style="
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          gap: 15px;
          width: 100%;
          min-width: 0;
          align-items: center;
        "
        v-if="!outOfOffice"
      >
        <span class="text-left text-uppercase font-weight-regular text-ellipsis">
          {{ projectName }}
        </span>
        <span>
          <ElapsedTimeDisplay :startTime="startTime" />
          <span
            class="ml-3"
            v-if="
              isEditable &&
              project?.projectId !== 'SYSTEM_LUNCH-BREAK' &&
              !isEditDescription
            "
            style="font-size: 12px !important"
          >
            <v-icon style="margin-top: -10px" v-if="hasDescription"
              >mdi-pencil</v-icon
            >
            <v-icon style="margin-top: -10px" v-else>mdi-pencil-plus</v-icon>
          </span>
        </span>
      </span>

      <!-- Description Editor -->
      <span
        v-if="
          isEditable &&
          project?.projectId !== 'SYSTEM_LUNCH-BREAK' &&
          isEditDescription
        "
        style="pointer-events: auto; width: 100% !important"
        class="mt-3"
        @click.prevent.stop
      >
        <v-textarea
          ref="descriptionTextarea"
          auto-grow
          v-model="descriptionText"
          label="Enter Description"
          variant="outlined"
          rows="1"
          @keydown="handleKeydown"
        ></v-textarea>
        <v-btn variant="text" @click="saveDescription">OK</v-btn>
      </span>

      <!-- Description Display -->
      <span
        :style="{ whiteSpace: 'normal', wordBreak: 'break-word' }"
        class="font-italic font-weight-regular"
        v-if="!isEditDescription && hasDescription && !userDescriptionDisplay"
      >
        {{
          description.length > 50
            ? description.slice(0, 20) + "..."
            : description
        }}
      </span>
      <span
        :style="{ whiteSpace: 'normal', wordBreak: 'break-word' }"
        class="font-italic font-weight-regular"
        v-if="userDescriptionDisplay && hasDescription && showUserDescription"
      >
        {{ description }}
      </span>
    </span>
  </v-btn>
</template>

<script>
import ElapsedTimeDisplay from "./ElapsedTimeDisplay.vue";

export default {
  name: "OngoingProjectDetails",
  components: {
    ElapsedTimeDisplay,
  },
  props: {
    userName: {
      type: String,
    },
    surname: {
      type: String,
    },
    projectName: {
      type: String,
    },
    startTime: {
      type: [String, Number],
    },
    description: {
      type: String,
      default: "",
    },
    project: {
      type: Object,
    },
    color: {
      type: String,
      default: "primary",
    },
    variant: {
      type: String,
      default: "elevated",
    },
    customStyles: {
      type: [String, Object],
      default: () => ({}), // Default to empty object
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isEditable: {
      type: Boolean,
      default: true, // By default, the button is editable
    },
    userDescriptionDisplay: {
      type: Boolean,
      default: false,
    },
    showUserDescription: {
      type: Boolean,
      default: false,
    },
    outOfOffice: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isEditDescription: false,
      descriptionText: this.description, // Initialize with the provided description
    };
  },
  computed: {
    hasDescription() {
      return !!this.description?.trim();
    },
    pointerEventsStyle() {
      return this.isEditDescription ? "pointer-events: none;" : "";
    },
    fullName() {
      if (this.userName && this.surname) {
        return `${this.userName} ${this.surname}`;
      } else if (this.userName) {
        return this.userName;
      } else if (this.surname) {
        return this.surname;
      }
      return "";
    },
  },
  methods: {
    handleClick() {
      // Only enter edit mode if the button is editable
      if (this.isEditable && !this.isEditDescription) {
        this.isEditDescription = true;
        // Auto-focus the textarea after entering edit mode
        this.$nextTick(() => {
          const input = this.$refs.descriptionTextarea?.$el?.querySelector('textarea');
          input?.focus();
        });
      }
    },
    handleKeydown(event) {
      // Enter without Shift: submit (like clicking OK)
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        this.saveDescription();
      }
      // Shift+Enter: allow default behavior (new line)
    },
    async saveDescription() {
      this.$emit("save-description", this.descriptionText); // Emit the updated description to parent
      this.isEditDescription = false;
    },
  },
};
</script>